// src/pages/podcast_public/OFSCPodcastPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { motion, useAnimation } from "framer-motion";
import { logPodcastAction } from "../../helpers/AnalyticsTags";
import "./PodcastPage.css";               /* 👉 create this with the styles below */

const AUDIO_PATH = "Podcast media/OFSC Top 5 steps_v7.wav";

/* jump-to chapters (seconds) ------------------------------------------------- */
const CHAPTERS = [
  {
    title: "Introduction",
    start: 0,
    desc: "Five steps for Wildfire Preparedness.",
    cta: null,
    image: "oakland_firesafe_davinci_compressed.jpg",
    defaultImage: true
  },
  {
    title: "Step 1 – Register for AC Alerts",
    start: 26,
    desc: "Sign up for Alameda County’s official emergency alert system to stay informed.",
    cta: { text: "Register for AC Alerts", url: "https://www.alamedacountyca.gov/emergencysite/emergency-site.htm" },
    image: "ac_alert_davinci_compressed.jpg"
  },
  {
    title: "Step 2 – Know Your Zones",
    start: 55,
    desc: "Locate & memorise your evacuation zones to leave quickly when asked.",
    cta: { text: "Know your Zone here", url: "https://protect.genasys.com/search" },
    image: "know_your_zone_davinci_compressed.jpg"
  },
  {
    title: "Step 3 – Family Emergency Plan",
    start: 83,
    desc: "Create and practice a family evacuation & communications plan.",
    cta: { text: "Family Emergency Plan Guide", url: "https://oaklandfiresafecouncil.org/wp-content/uploads/OFSC-Guide-05-Emergency-Planning.pdf" },
    image: "family_wildfire_meetup_compressed.jpg"
  },
  
  {
    title: "Step 4 – Go-Bags",
    start: 114,
    desc: "Prepare emergency go-bags with supplies for at least 72 hours.",
    cta: { text: "Go-bag guide", url: "https://oaklandfiresafecouncil.org/wp-content/uploads/OFSC-Guide-07-Evacuation-and-Go-Bag-Checklists.pdf" },
    image: "wildfire_go_bag_davinci_compressed.jpg"
  },
  {
    title: "Step 5 – Help Your Community",
    start: 148,
    desc: "Check on neighbours, share info, and volunteer with local response groups.",
    cta: { text: "Help your community", url: "https://oaklandfiresafecouncil.org/wp-content/uploads/OFSC-Guide-01-Community-Organization.pdf" },
    image: 'community_wildfire_evac_compressed.jpg'
  },
  {
    title: "Conclusion",
    start: 174,
    desc: "Check on neighbours, share info, and volunteer with local response groups.",
    cta: { text: "OFSC website", url: "https://oaklandfiresafecouncil.org/" },
  }
];

export default function PodcastPage() {
  const storage   = getStorage();
  const db        = getFirestore();
  const audioRef  = useRef(null);
  const progress  = useRef(null);
  const [duration, setDuration]       = useState(0);
  const [current,  setCurrent]        = useState(0);
  const [isPlaying, setIsPlaying]     = useState(false);
  const [audioSrc,  setAudioSrc]      = useState("");
  const [email,     setEmail]         = useState("");
  const [agree,     setAgree]         = useState(false);
  const [status,    setStatus]        = useState("");
  
  const [chapIdx, setChapIdx] = useState(0);
  const chapIdxRef = useRef(0);
  useEffect(() => { chapIdxRef.current = chapIdx; }, [chapIdx]);

  const defaultImageName = CHAPTERS.find(c => c.defaultImage)?.image;
  const [imgUrls, setImgUrls] = useState({});
  const chapter = CHAPTERS[chapIdx];
  const currentImg =
  (chapter.image && imgUrls[chapter.image]) || imgUrls[defaultImageName];

  /* fetch audio on mount ---------------------------------------------------- */
  useEffect(() => {
    getDownloadURL(storageRef(storage, AUDIO_PATH))
      .then(setAudioSrc)
      .catch((e) => console.error("Audio fetch error:", e));
  }, [storage]);

  useEffect(() => {
    const load = async () => {
      const urls = {};
  
      /* 1 · fetch default first */
      if (defaultImageName) {
        try {
          urls[defaultImageName] = await getDownloadURL(
            storageRef(storage, `Podcast_images/OFSC_five_steps/${defaultImageName}`)
          );
        } catch (e) { console.error(e); }
      }
  
      /* 2 · fetch the rest in parallel */
      await Promise.all(
        CHAPTERS
          .map(c => c.image)
          .filter(name => name && name !== defaultImageName)
          .map(async name => {
            try {
              urls[name] = await getDownloadURL(
                storageRef(storage, `Podcast_images/OFSC_five_steps/${name}`)
              );
            } catch (e) { console.error(e); }
          })
      );
  
      setImgUrls(urls);
    };
    load();
  }, [storage]);

 

  /* sync progress bar ------------------------------------------------------- */
  useEffect(() => {
    if (!audioRef.current) return;
    const a = audioRef.current;
    const update = () => {
      setCurrent(a.currentTime);
    
      /* determine active chapter */
      const idx = CHAPTERS.findIndex((c, i) =>
        a.currentTime >= c.start &&
        (i + 1 === CHAPTERS.length || a.currentTime < CHAPTERS[i + 1].start)
      );
      
      if (idx !== chapIdxRef.current && idx !== -1) {
        logPodcastAction({
          platform : "Desktop Web",
          action   : "Auto chapter",
          cta      : "Autoplay",
          chapter  : CHAPTERS[idx].title,
          podcast  : "OFSC - Five steps",
        });
        setChapIdx(idx);
      }
    };
    a.addEventListener("timeupdate", update);
    a.addEventListener("loadedmetadata", () => setDuration(a.duration));
    return () => {
      a.removeEventListener("timeupdate", update);
    };
  }, [audioSrc]);

  /* utility ----------------------------------------------------------------- */
  const fmt   = (t) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };
  const seek  = (t) => {
    if (audioRef.current) {
      audioRef.current.currentTime = t;
      if (!isPlaying) togglePlay();
    }
  };
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else           audioRef.current.play();

    logPodcastAction({
      platform : "Desktop Web",
      action   : "Play/Pause",
      cta      : isPlaying ? "Pause" : "Play",
      chapter  : chapter.title,
      podcast  : "OFSC - Five steps",
    });

    setIsPlaying(!isPlaying);
  };

  /* email submit ------------------------------------------------------------ */
  const subscribe = async (e) => {
    e.preventDefault();
    if (!agree) return;
    setStatus("Submitting…");
    try {
      await addDoc(collection(db, "podcast_subscribers"), {
        email: email.trim().toLowerCase(),
        createdAt: serverTimestamp(),
        topic: "OFSC",
      });
      setStatus("Thank you for subscribing!");
      setEmail("");
      setAgree(false);
    } catch (err) {
      console.error(err);
      setStatus("Error – please try again.");
    }
  };

  /* ------------------------------------------------------------------------ */
  return (
    <div className="pod-wrap">

      {/* ───────────────  SECTION 1 : AUDIO PLAYER  ─────────────── */}
      <section className="pod-card">
        <header className="pod-header">
          <div>
            <h1 className="pod-title">Wildfire Evacuation: Quick Guide</h1>
            <p className="pod-author">Oakland&nbsp;FireSafe&nbsp;Council</p>
            <p className="pod-subtitle">
              Listen to essential wildfire safety tips from the Oakland Fire Safe Council to keep you and your
              family safe.
            </p>
          </div>
          <span className="tag-ai">AI Narrated</span>
        </header>

        {/* Artwork / hero image */}
        <motion.div
          initial={{ scale: 1.04 }}
          animate={{ scale: 1 }}
          transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
          className="pod-art"
          style={{ backgroundImage: `url(${currentImg || ""})` }}
        >
          <div className="pod-art-caption">
            <h3 className="pod-caption-title">{chapter.title}</h3>

            {chapter.cta && (
              <a
                href={chapter.cta.url}
                target="_blank"
                rel="noreferrer"
                className="pod-cta"
                onClick={() => {
                  logPodcastAction({
                    platform : "Desktop Web",
                    action   : "CTA",
                    cta      : chapter.cta.text,
                    chapter  : chapter.title,
                    podcast  : "OFSC - Five steps",
                  });
                }}
              >
                {chapter.cta.text}
              </a>
            )}
          </div>
        </motion.div>

        {/* custom progress bar */}
        <div
          className="pod-progress"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const pct  = (e.clientX - rect.left) / rect.width;
            seek(pct * duration);
          }}
        >
          <div
            className="pod-progress__bar"
            style={{ width: `${(current / duration) * 100 || 0}%` }}
          />
        </div>

        {/* time + controls */}
        <div className="pod-controls">
          <span>{fmt(current)}</span>

          <button className="pod-btn" onClick={() => seek(Math.max(current - 15, 0))}>
            ‹‹
          </button>

          <button className="pod-btn pod-btn--main" onClick={togglePlay}>
            {isPlaying ? "❚❚" : "►"}
          </button>

          <button className="pod-btn" onClick={() => seek(Math.min(current + 15, duration))}>
            ››
          </button>

          <span>{fmt(duration)}</span>
        </div>

        <audio ref={audioRef} src={audioSrc} preload="metadata" />

      </section>

      {/* ───────────────  SECTION 2 : CHAPTERS   ─────────────── */}
      <section className="pod-chapters">
        <h2>Jump to Specific Section</h2>
        {CHAPTERS.map((c, i) => (
          <div
            key={i}
            className="chapter"
            onClick={() => {
              logPodcastAction({
                platform : "Desktop Web",
                action   : "Chapter change",
                cta      : "Chapter button",
                chapter  : c.title,
                podcast  : "OFSC - Five steps",
              });
              seek(c.start);
            }}
          >
            <div className="chapter__num">{i + 1}</div>
            <div className="chapter__body">
              <h3>{c.title}</h3>
              <small>{fmt(c.start)} – {fmt(i + 1 < CHAPTERS.length ? CHAPTERS[i + 1].start - 1 : duration)}</small>
              <p>{c.desc}</p>
            </div>
          </div>
        ))}
      </section>

      <p className="pod-caption">
        This audio guide was created using AI, drawing from materials
        developed by the Oakland&nbsp;Fire&nbsp;Safe&nbsp;Council. <br/>We acknowledge and
        thank them for their foundational work in wildfire preparedness.
      </p>

      {/* ───────────────  SECTION 3 : EMAIL FORM ─────────────── 
      <section className="pod-form">
        <h2>Get Personalized Wildfire Safety Podcasts</h2>
        <p>
          Enter your email to receive location-specific wildfire safety
          information tailored to your needs.
        </p>

        <form onSubmit={subscribe}>
          <input
            type="email"
            placeholder="Your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <label className="chk">
            <input
              type="checkbox"
              checked={agree}
              onChange={() => setAgree(!agree)}
              required
            />
            <span>I agree to receive personalized wildfire safety information via email</span>
          </label>

          <button type="submit" disabled={!agree || !email}>
            Subscribe for Updates
          </button>
        </form>

        {status && <p className="status">{status}</p>}
      </section>
      */}
    </div>
  );
}